import { useContext, useEffect, useState } from 'react'

import { getDealer } from '@/services/dealer.service'
import { UserStoreContext } from '@/stores/UserStore'

// Every page that shows the dealer header asks for the same shape, so they share one
// cache entry. Outstanding List, Billing, the Dashboard, and Log ROs all read it.
const EXPAND = 'group,manufacturer'

// Dealers already loaded this session, keyed by user and dealer. Keyed by user as well
// so a different login in the same tab never sees the previous user's copy.
const cache = new Map()

const keyFor = (userId, dealerId) => `${userId || 'anon'}:${dealerId}`

/**
 * The dealer for `dealerId`, served from the session cache when there is one.
 *
 * A cached dealer renders at once, so moving between dealer pages does not flash the
 * header skeleton. It is still refetched every time in the background, so an edit made
 * elsewhere shows up on the next visit rather than never.
 *
 * @returns {{ dealer: object|null, error: string|null }}
 */
export const useDealer = (dealerId) => {
  const { user } = useContext(UserStoreContext)
  const key = keyFor(user?.id, dealerId)

  const [dealer, setDealer] = useState(() => cache.get(key) || null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!dealerId) return undefined
    const controller = new AbortController()

    // Show the cached copy for the new dealer straight away, or clear the old one so
    // the header shows its skeleton rather than the previous dealer's name. Also drop
    // a prior error so a failure on one dealer does not stick after switching.
    setDealer(cache.get(key) || null)
    setError(null)

    getDealer(
      dealerId,
      () => {},
      (message) => {
        if (controller.signal.aborted) return
        cache.delete(key)
        setDealer(null)
        setError(message)
      },
      () => {},
      EXPAND,
      controller.signal,
    ).then((response) => {
      // `response` is null when the request was superseded (aborted); ignore it so a
      // stale dealer can never overwrite the current one.
      if (!response || controller.signal.aborted) return
      cache.set(key, response)
      setDealer(response)
    })

    return () => controller.abort()
  }, [key])

  return { dealer, error }
}
